import { getCookie, deleteCookie, buildRequest } from "./auth.js";
import UnauthorisedError from '../errors/UnauthorisedError.js';

function sanitizePrescription(order) {
    if (order.prescription) {
      const prescription = order.prescription;
  
      const updateNulls = (obj) => {
        for (let key in obj) {
          if (obj[key] === null) {
            obj[key] = 0;
          } else if (typeof obj[key] === "object" && obj[key] !== null) {
            updateNulls(obj[key]);
          }
        }
      };
  
      if (prescription.rightEye) {
        updateNulls(prescription.rightEye);
      }
      if (prescription.leftEye) {
        updateNulls(prescription.leftEye);
      }
    }
  }

export async function createPrivateOrder(privateOrder) {
    const url = `/Optician/CompletePrivateOrder`;
    sanitizePrescription(privateOrder);

    try {
        const response = await buildRequest(url, 'POST', privateOrder);
        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else if (text.includes("The Promo code is not valid")) {
                    return "Invalid PromoCode";
                }
                else {
                    throw new Error(`Failed to create Private Order: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else if (text.includes("The Promo code is not valid")) {
                    return "Invalid PromoCode";
                }
                else {
                    throw new Error(`Failed to create Private Order: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function processNonEyeTicketRequest(eyeTicketId, barcode, eyewearType, eyeticketRequirement) {
    const url = `/EyeTicketProcessing/ProcessEmployeeVisionRequest?iEyeTicketID=${encodeURIComponent(eyeTicketId)}&cBarcode=${encodeURIComponent(barcode)}&EmployeeRequirement=${encodeURIComponent(eyeticketRequirement)}&EyewearType=${encodeURIComponent(eyewearType)}`;

    try {
        const response = await buildRequest(url, 'POST');

        if (!response.ok) {
            if (response.status === 404 || response.status === 500 || response.status === 400) {
                console.warn('Eyeticket Failed To Process');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Eyeticket Failed To Process: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchOpticiansByPostcode(postcode, maxFee = 0) {
    const url = `/Optician/OpticiansSearch/${encodeURIComponent(postcode)}/15/GB/0/${encodeURIComponent(maxFee)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404 || response.status === 500 || response.status === 400) {
                console.warn('No opticians found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Opticians: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchFavouriteOpticians(maxFee = 0) {
    const url = `/Optician/GetFavouriteOpticians/${encodeURIComponent(maxFee)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Favourite Opticians: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function addFavouriteOptician(optician, account) {
    const url = `/EndUser/AddFavOptician`;
    const payload = {
        opticianID: optician.opticianID,
        enduser: optician.enduser,
        enduser_ac_code: account,
        enduser_co_name: optician.enduser_co_name,
        opticians_onsite_ID: optician.opticians_onsite_ID
    };

    try {
        const response = await buildRequest(url, 'POST', payload);
        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to add favourite optician: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to add favourite optician: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function removeFavouriteOptician(id) {
    const url = `/enduser/DeleteFavOptician/${encodeURIComponent(id)}`;

    try {
        const response = await buildRequest(url, 'DELETE');
        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to remove favourite optician: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to add remove optician: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchPrivateFrames(iPageSize = 200, intPageNumber = 1) {
    let url = `/Optician/GetPrivateFramesList/${encodeURIComponent(iPageSize)}/${encodeURIComponent(intPageNumber)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Frames found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Frames: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchPrivateOrders(account, page, pageSize, search) {
    let url = `/Optician/GetOpticianPrivateOrdersHistory/${encodeURIComponent(pageSize)}/${encodeURIComponent(page)}`;

    if (account || search) {
        url += '?';

        if (account) {
            url += `cAccountIDFilter=${encodeURIComponent(account)}`;
        }

        if (search) {
            if (account) {
                url += '&';
            }
            url += `cGlobalSearch=${encodeURIComponent(search)}`;
        }
    }

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Frames found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Frames: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchLensDropDownSelections() {
    let url = `/Optician/GetLensDropDownSelection`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Lens Options found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Lens Options: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchLensBrand(frameId, lensMaterial, lensType) {
    let url = `/Optician/GetPrivateLensBrand/${encodeURIComponent(frameId)}?cSelectedLensMaterial=${encodeURIComponent(lensMaterial)}&cSelectedLensType=${encodeURIComponent(lensType)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Lens Brand found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Lens Brand: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchCoatsAndTintsForLens(lensId) {
    let url = `/Optician/GetPrivateApprovedCoatsTintsAndCases?iLensID=${encodeURIComponent(lensId)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Coats or Tints found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Coats and Tints : ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchEyewearOptions(lensId) {
    let url = `/Optician/GetPrivateApprovedCoatsTintsAndCases?iLensID=${encodeURIComponent(lensId)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Coats or Tints found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Coats and Tints : ${response.status} ${response.statusText}`);
            }
        }

        const res = await response.json();
        return res.Cases;
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchPrescriptionMeasures(lensId, frameId, singleVisionType) {
    switch (singleVisionType) {
        case "Distance":
            singleVisionType = 0;
            break;
        case "Readers":
            singleVisionType = 1;
            break;
        case "Intermediate":
            singleVisionType = 2;
            break;
        default:
            singleVisionType = 1;
            break;
    }

    let url = `/Optician/GetPrivateStandardPrescriptionMeasures/${encodeURIComponent(lensId)}/${encodeURIComponent(frameId)}/${encodeURIComponent(singleVisionType)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No Prescription Measures found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch Prescription Measures : ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}



